import { Banner, Box, Button, Grid, GridColumn, Text } from "@modernatx-internal/ui-kit-react";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";

import { PageAppState } from "@/components/PageAppState";
import { PageLayout } from "@/components/PageLayout";
import { RichText } from "@/components/RichText";
import { VialImage } from "@/components/VialImage";
import { useInitializeRouter } from "@/hooks/useInitializeRouter";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useText } from "@/hooks/useText";
import { ClientAppData, Unit } from "@/types";
import { fetchAppData } from "@/utils/fetchAppData";
import { getCheckFormStorage } from "@/utils/getCheckFormStorage";
import { getClientAppData } from "@/utils/getClientAppData";
import { getLogger } from "@/utils/getLogger";

export const getServerSideProps: GetServerSideProps<ClientAppData> = async ({ req, res }) => {
  const appData = await fetchAppData(req, res);
  const clientAppData = getClientAppData(appData);
  const { routingStrategy } = appData;

  getLogger().logRequest({
    appData,
    req,
    responseCode: res.statusCode
  });

  return {
    ...(routingStrategy?.redirect ? routingStrategy : {}),
    props: clientAppData
  };
};

const TemperatureExcursionToolLandingPage = ({
  guidanceMissing
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { getLocalizedPath } = useLocalizedPath();
  const checkFormStorage = React.useMemo(() => getCheckFormStorage(), []);
  const { getText } = useText();
  const handleGetStartedClick = React.useCallback(() => {
    checkFormStorage.removeState();
  }, [checkFormStorage]);
  const renderUnit = React.useCallback((unit: Unit) => {
    return unit === "CELSIUS" ? "°C" : "°F";
  }, []);

  useInitializeRouter();

  return (
    <Grid variant="uniform-12">
      {!!guidanceMissing?.length && (
        <GridColumn>
          <Banner
            text={
              <>
                {guidanceMissing?.map(
                  (
                    {
                      countryCode,
                      minuteRange,
                      packaging,
                      product,
                      storage,
                      temperatureRange,
                      unit
                    },
                    i
                  ) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        gap: 4
                      }}
                    >
                      <Box>
                        <b>Country:</b> {countryCode}
                      </Box>
                      <Box>
                        <b>Product:</b> {product}
                      </Box>
                      <Box>
                        <b>Packaging:</b> {packaging}
                      </Box>
                      <Box>
                        <b>Storage:</b> {storage}
                      </Box>
                      <Box>
                        <b>Minutes:</b>{" "}
                        {minuteRange[0] === minuteRange[1]
                          ? `${minuteRange[0]}`
                          : `${minuteRange[0]} to ${minuteRange[1]}`}
                      </Box>
                      <Box>
                        <b>Temperature:</b>{" "}
                        {temperatureRange[0] === temperatureRange[1]
                          ? `${temperatureRange[0]}${renderUnit(unit)}`
                          : `${temperatureRange[0]}${renderUnit(unit)} to ${temperatureRange[1]}${renderUnit(unit)}`}
                      </Box>
                    </Box>
                  )
                )}
              </>
            }
            title="The following scenarios are missing guidance"
            variant="critical"
          />
        </GridColumn>
      )}
      <GridColumn size={[12, 12, 8]} sx={{ py: [4, null, 15] }}>
        <Text as="h1" variant={["h4", null, "h2"]} sx={{ color: "text-emphasis" }}>
          {getText("TEMPERATURE_EXCURSION_CHECKER")}
        </Text>
        <Box
          sx={{
            display: ["block", null, "none"],
            mt: 8
          }}
        >
          <VialImage sx={{ maxHeight: 36 }} />
        </Box>
        <Box
          sx={{
            fontSize: "font-size-5",
            pt: 8
          }}
        >
          <RichText text={getText("TEMPERATURE_EXCURSION_CHECKER_DESCRIPTION")} />
        </Box>
        <Box sx={{ py: 10 }}>
          <Button
            icon="arrow-right"
            iconPosition="end"
            href={getLocalizedPath("/temperature-excursion/check")}
            onClick={handleGetStartedClick}
            size="large"
          >
            {getText("GET_STARTED")}
          </Button>
        </Box>
      </GridColumn>
      <GridColumn
        size={[12, 12, 4]}
        sx={{
          display: ["none", null, "flex"],
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <VialImage sx={{ maxHeight: 64 }} />
      </GridColumn>
    </Grid>
  );
};

const TemperatureExcursionToolLandingPageWithLayout = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) => (
  <PageLayout {...props}>
    <TemperatureExcursionToolLandingPage {...props} />
    <PageAppState />
  </PageLayout>
);

export default TemperatureExcursionToolLandingPageWithLayout;
